import React, { useState, useEffect, useMemo, useRef } from 'react'

import { DEFAULT, PRODUCT_PAGE, FULFILLED, REJECTED, CART, BUILDING_KIT_SKU } from '../../../constants/constants'
import { getCheckoutcart } from '../../../store/requests/apiRequests/getCheckoutcart'
import { flattenBom } from '../../../helpers/flattenBom'
import { getArticleLabel } from '../../../helpers/getArticleLabel'
import {
  setIsAddToCartPromptModalOpen,
  setIsAddToCartConfirmedModalOpen,
  setIsAddToCartAdditionalPromptModalOpen,
  setIsAddToCartErrorModalOpen,
  setIsAddToCartUnavailableModalOpen,
  setIsMobileMenuOpen,
  setIsSummaryPreviewExpanded,
} from '../../../store/ui/uiSlice'
import ArticlesList from '../../ArticlesList/ArticlesList'
import Heading from '../../Heading/Heading'
import Modal from '../../Modal/Modal'
import * as commonStyles from '../../styles'
import * as styles from './styles'
import { useAppDispatch, useAppSelector } from '../../../hooks'

const Styled = {
  ...commonStyles,
  ...styles,
}

interface Props {
  onRequestSaveProject: (args: { type: string }) => {
    internalSaveUuidv4: string | null
  }
  insideSummaryPanel?: boolean
  testId?: string
}

const AddToCart: React.FC<Props> = ({ onRequestSaveProject, insideSummaryPanel, testId }) => {
  const dispatch = useAppDispatch()

  const { errors: bundleErrors, articles: bundleArticles, articlesTotal } = useAppSelector((state) => state.bundle)
  const { bom } = useAppSelector((state) => state.configResult)
  const { applicationMode, content } = useAppSelector((state) => state.init)
  const { unresolvedErpItemNumbers, substitutedArticleTitles } = useAppSelector((state) => state.articleSubstitutions)
  const { latestFulfilledId } = useAppSelector((state) => state.internalSaveEvent)
  const { apiConfigs } = useAppSelector((state) => state.requests)
  const { requestStatus_fetchPrices_aggregated, requestStatus_addToCart_aggregated } = useAppSelector(
    (state) => state.requests.requestStatus
  )
  const {
    isAddToCartPromptModalOpen,
    isAddToCartConfirmedModalOpen,
    isAddToCartAdditionalPromptModalOpen,
    isAddToCartErrorModalOpen,
    isAddToCartUnavailableModalOpen,
    isMobileMenuOpen,
    isSummaryPreviewExpanded,
  } = useAppSelector((state) => state.ui)

  // true when waiting for savefile from DM iframe
  // **OR** waiting for thunks/addToCart_aggregated promise
  const [isRequestPending, setIsRequestPending] = useState(false)

  const [internalSaveUuidv4, setInternalSaveUuidv4] = useState('')

  const previousBundleErrorsRef = useRef(bundleErrors)

  // unresolvedErpItemNumbers is never emptied, so an article the customer has configured away would block the cart forever
  const unresolvedInConfig = useMemo(() => {
    const skusInConfig = new Set(flattenBom(bom ?? []).map(({ sku }) => sku))
    return unresolvedErpItemNumbers.filter((erpItemNumber) => skusInConfig.has(erpItemNumber))
  }, [bom, unresolvedErpItemNumbers])

  const labelsForUnavailableArticles = useMemo(() => {
    // A null Sku is an article Scensum doesn't recognise; it is named from unresolvedInConfig below instead
    // FSA001 is the structure article a whole BUILDING_KIT section collapses to, and is nothing the customer could act on
    const notForPurchase = (bundleArticles ?? []).filter(
      (article) => article.IsAvailableForPurchase === false && article.Sku !== null && article.Sku !== BUILDING_KIT_SKU
    )

    return notForPurchase
      .map((article) => getArticleLabel(article.Title, article.Sku, content?.other?.artNo))
      .concat(
        unresolvedInConfig.map((erpItemNumber) =>
          getArticleLabel(substitutedArticleTitles[erpItemNumber], erpItemNumber, content?.other?.artNo)
        )
      )
  }, [bundleArticles, unresolvedInConfig, substitutedArticleTitles, content])

  const handleAddToCart = (): void => {
    setIsRequestPending(true)
    const { internalSaveUuidv4 } = onRequestSaveProject({ type: CART })
    if (internalSaveUuidv4) setInternalSaveUuidv4(internalSaveUuidv4)
  }

  useEffect(() => {
    if (isRequestPending) {
      if (requestStatus_addToCart_aggregated === FULFILLED) {
        setIsRequestPending(false)

        if (latestFulfilledId === internalSaveUuidv4) {
          dispatch(setIsAddToCartPromptModalOpen(false))
          dispatch(setIsAddToCartConfirmedModalOpen(true))

          if (applicationMode === PRODUCT_PAGE) {
            getCheckoutcart({
              apiConfigs,
              onSuccess: ({ data: html }) => {
                if (window.updateCartQuantity) {
                  // Call function from Scensum to update cart preview in website header
                  window.updateCartQuantity(html)
                } else {
                  console.error('window.updateCartQuantity not found')
                }
              },
            })
          }
        } else {
          console.error('Unexpected error in AddToCart component; latestFulfilledId !== internalSaveUuidv4')
        }
      }

      if (requestStatus_addToCart_aggregated === REJECTED) {
        setIsRequestPending(false)
        dispatch(setIsAddToCartPromptModalOpen(false))
        dispatch(setIsAddToCartErrorModalOpen(true))
      }
    }
  }, [requestStatus_addToCart_aggregated])

  useEffect(() => {
    // Only on the transition into an error state, so that a refetched price doesn't reopen a modal the customer closed
    if (bundleErrors && !previousBundleErrorsRef.current) {
      dispatch(setIsAddToCartUnavailableModalOpen(true))
    }
    previousBundleErrorsRef.current = bundleErrors
  }, [bundleErrors])

  return (
    <>
      <Styled.CTA
        applicationMode={applicationMode}
        $insideSummaryPanel={insideSummaryPanel}
        // Adding className to apply Scensum styling
        className={`button-primary${applicationMode === PRODUCT_PAGE ? '-large' : ''}`}
        disabled={requestStatus_fetchPrices_aggregated !== FULFILLED}
        data-testid={`add-to-cart-button${insideSummaryPanel ? '-summary-panel' : ''}${applicationMode === PRODUCT_PAGE ? '-product-page' : ''}`}
        onClick={() => {
          if (isMobileMenuOpen) dispatch(setIsMobileMenuOpen(false))
          if (isSummaryPreviewExpanded) dispatch(setIsSummaryPreviewExpanded(false))

          // An article DM could not replace means the configuration is no longer trusted, so nothing is sent to the cart
          if (bundleErrors || unresolvedInConfig.length) {
            dispatch(setIsAddToCartUnavailableModalOpen(true))
            return
          }

          if (requestStatus_addToCart_aggregated === FULFILLED) {
            dispatch(setIsAddToCartAdditionalPromptModalOpen(true))
            return
          }

          dispatch(setIsAddToCartPromptModalOpen(true))
        }}
      >
        {!insideSummaryPanel && applicationMode === DEFAULT && <i className="fas fa-cart-plus" aria-hidden="true" />}
        <span className="button-text">{content?.buttons?.addToCart}</span>
      </Styled.CTA>

      <Modal
        isOpen={isAddToCartPromptModalOpen}
        onRequestClose={() => {
          dispatch(setIsAddToCartPromptModalOpen(false))
        }}
        contentLabel={content?.modals?.addToCartPrompt?.title}
        testId={`add-to-cart-prompt-modal-${applicationMode}-${testId}`}
      >
        <Styled.ModalAddToCart>
          <Heading level={2}>{content?.modals?.addToCartPrompt?.title}</Heading>
          <p>
            {content?.modals?.addToCartPrompt?.description} {articlesTotal}
            &nbsp;{content?.modals?.addToCartPrompt?.description_2}
          </p>
          <Styled.Button
            className="button-secondary"
            disabled={isRequestPending}
            onClick={() => {
              dispatch(setIsAddToCartPromptModalOpen(false))
            }}
          >
            {content?.buttons?.cancel}
          </Styled.Button>
          <Styled.Button
            className="button-primary"
            disabled={requestStatus_fetchPrices_aggregated !== FULFILLED || isRequestPending}
            isLoading={isRequestPending}
            data-testid="confirm-add-to-cart-button"
            onClick={() => {
              handleAddToCart()
            }}
          >
            {content?.buttons?.addToCart}
          </Styled.Button>
          <ArticlesList isAddToCartPrompt />
        </Styled.ModalAddToCart>
      </Modal>
      <Modal
        isOpen={isAddToCartConfirmedModalOpen}
        onRequestClose={() => {
          dispatch(setIsAddToCartConfirmedModalOpen(false))
        }}
        contentLabel={content?.modals?.addToCartConfirmed?.title}
        testId={`add-to-cart-confirmed-modal-${applicationMode}-${testId}`}
      >
        <Styled.ModalAddToCart>
          <Heading level={2}>{content?.modals?.addToCartConfirmed?.title}</Heading>
          <p>
            {content?.modals?.addToCartConfirmed?.description} {articlesTotal}
            &nbsp;{content?.modals?.addToCartConfirmed?.description_2}
          </p>
          <Styled.Button
            className="button-secondary"
            onClick={() => {
              dispatch(setIsAddToCartConfirmedModalOpen(false))
            }}
          >
            {content?.buttons?.close}
          </Styled.Button>
          <Styled.Button
            className="button-primary-arrow"
            id="to-checkout-button"
            onClick={() => {
              window.location.href = `${apiConfigs.scensumBaseUrl}/checkout/`
            }}
            data-testid="to-checkout-button"
          >
            {content?.buttons?.toCheckout}
          </Styled.Button>
        </Styled.ModalAddToCart>
      </Modal>
      <Modal
        isOpen={isAddToCartAdditionalPromptModalOpen}
        onRequestClose={() => {
          dispatch(setIsAddToCartAdditionalPromptModalOpen(false))
        }}
        contentLabel={content?.modals?.addToCartAdditionalPrompt?.title}
      >
        <Styled.ModalAddToCart>
          <Heading level={2}>{content?.modals?.addToCartAdditionalPrompt?.title}</Heading>
          <p>{content?.modals?.addToCartAdditionalPrompt?.description}</p>
          <p>{content?.modals?.addToCartAdditionalPrompt?.description_2}</p>
          <Styled.Button
            className="button-primary"
            onClick={() => {
              dispatch(setIsAddToCartAdditionalPromptModalOpen(false))
            }}
          >
            {content?.buttons?.cancel}
          </Styled.Button>
          <Styled.Button
            className="button-secondary"
            onClick={() => {
              dispatch(setIsAddToCartAdditionalPromptModalOpen(false))
              dispatch(setIsAddToCartPromptModalOpen(true))
            }}
          >
            {content?.buttons?.proceed}
          </Styled.Button>
        </Styled.ModalAddToCart>
      </Modal>
      <Modal
        isOpen={isAddToCartErrorModalOpen}
        onRequestClose={() => {
          dispatch(setIsAddToCartErrorModalOpen(false))
        }}
        contentLabel={content?.modals?.addToCartError?.title}
      >
        <Styled.ModalAddToCart>
          <Heading level={2}>{content?.modals?.addToCartError?.title}</Heading>
          <p>{content?.modals?.addToCartError?.description}</p>
          <p>{content?.modals?.addToCartError?.description_2}</p>
          <Styled.Button
            className="button-secondary"
            onClick={() => {
              dispatch(setIsAddToCartErrorModalOpen(false))
            }}
          >
            {content?.buttons?.close}
          </Styled.Button>
        </Styled.ModalAddToCart>
      </Modal>
      <Modal
        isOpen={isAddToCartUnavailableModalOpen}
        onRequestClose={() => {
          dispatch(setIsAddToCartUnavailableModalOpen(false))
        }}
        contentLabel={content?.modals?.addToCartUnavailable?.title}
      >
        <Styled.ModalAddToCart>
          <Heading level={2}>{content?.modals?.addToCartUnavailable?.title}</Heading>
          <p>{content?.modals?.addToCartUnavailable?.description}</p>
          {labelsForUnavailableArticles.length > 0 ? (
            <>
              <p>{content?.modals?.addToCartUnavailable?.articlesHeading}</p>
              <Styled.UnavailableArticlesList>
                {labelsForUnavailableArticles.map((label: string) => (
                  <li key={label}>{label}</li>
                ))}
              </Styled.UnavailableArticlesList>
            </>
          ) : (
            // Nothing to name: the only unavailable article was FSA001, filtered out above as unactionable
            <p>{content?.modals?.addToCartUnavailable?.buildingKit}</p>
          )}
          <Styled.Button
            className="button-secondary"
            onClick={() => {
              dispatch(setIsAddToCartUnavailableModalOpen(false))
            }}
          >
            {content?.buttons?.close}
          </Styled.Button>
        </Styled.ModalAddToCart>
      </Modal>
    </>
  )
}

export default AddToCart
